import React, { createContext, useContext, useEffect, useState } from "react";
import { translations, t as tt } from "@/lib/i18n";
import { resolveInitialLang } from "@/lib/lang";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  // Immediate paint uses localStorage so the toggle stays instant on boot.
  // Once /auth/me hydrates (via AuthContext), we reconcile against the
  // server-persisted `user.language` — see the seeding effect below.
  const [lang, setLang] = useState(() => {
    try {
      return resolveInitialLang({
        userLang: null,
        storedLang: localStorage.getItem("af_lang"),
      });
    } catch {
      return "en";
    }
  });

  const { user } = useAuth() || {};

  // Seed from user profile on mount / when the user object appears.
  // Server value wins over localStorage (see resolveInitialLang precedence).
  // Guard with a ref-style flag so we only overwrite localStorage-derived
  // state once per user hydration — user's subsequent toggles stay authoritative.
  const [seededFromUser, setSeededFromUser] = useState(false);
  useEffect(() => {
    if (!user) return;
    if (seededFromUser) return;
    const next = resolveInitialLang({
      userLang: user.language,
      storedLang: localStorage.getItem("af_lang"),
    });
    setLang(next);
    setSeededFromUser(true);
  }, [user, seededFromUser]);

  // Persist to localStorage + <html> attributes on every change.
  useEffect(() => {
    localStorage.setItem("af_lang", lang);
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
  }, [lang]);

  // Background sync to the server whenever the user toggles.
  // Only fires once the user is authenticated and after the initial seed
  // has run — this avoids a redundant PATCH echoing back the server's
  // own value on hydration. Failures are swallowed; localStorage remains
  // the immediate source of truth so the UI never blocks on the PATCH.
  useEffect(() => {
    if (!user) return;
    if (!seededFromUser) return;
    if (user.language === lang) return;
    api.patch("/auth/me/language", { language: lang }).catch(() => { /* silent */ });
  }, [lang, user, seededFromUser]);

  const dict = translations[lang] || translations.en;
  const t = (path) => tt(dict, path);
  const toggle = () => setLang((l) => (l === "en" ? "ar" : "en"));

  return (
    <I18nCtx.Provider value={{ lang, setLang, toggle, t, dict }}>
      {children}
    </I18nCtx.Provider>
  );
}

export const useI18n = () => useContext(I18nCtx);
